import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const ROLES = [
  { key: "individual", title: "Individual therapist", price: "$49.99 / deal / month", desc: "Independent licensed therapist." },
  { key: "business", title: "Massage business", price: "$99.99 / deal / month", desc: "Studio, spa, or wellness clinic." },
  { key: "customer", title: "I'm a customer", price: "Free", desc: "Browse and book deals." },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("individual");
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, role };
      if (role !== "business") delete payload.business_name;
      const u = await register(payload);
      toast.success(`Welcome to Massage Savings, ${u.name}`);
      navigate(role === "individual" || role === "business" ? "/dashboard" : "/");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] text-center">Get started</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight text-center mt-2">Create your account</h1>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {ROLES.map(r => (
          <button
            key={r.key}
            type="button"
            data-testid={`role-${r.key}`}
            onClick={()=>setRole(r.key)}
            className={`text-left p-5 rounded-2xl border transition-all ${role===r.key ? 'border-[#D97757] bg-white shadow-[0_8px_28px_-12px_rgba(217,119,87,0.25)]' : 'border-[#E8E2D6] bg-white hover:border-[#D97757]/60'}`}
          >
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5F6C60]">{r.price}</div>
            <div className="font-serif-display text-xl mt-1">{r.title}</div>
            <div className="text-sm text-[#5F6C60] mt-1">{r.desc}</div>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-8 bg-white p-8 rounded-2xl border border-[#E8E2D6] space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Full name</Label>
            <Input data-testid="register-name" required value={form.name} onChange={update("name")} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Email</Label>
            <Input data-testid="register-email" type="email" required value={form.email} onChange={update("email")} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12" />
          </div>
          {role === "business" && (
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em]">Business name</Label>
              <Input data-testid="register-business-name" required value={form.business_name} onChange={update("business_name")} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12" />
            </div>
          )}
          {role !== "customer" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em]">Phone</Label>
              <Input data-testid="register-phone" value={form.phone} onChange={update("phone")} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12" />
            </div>
          )}
          <div className={role === "customer" ? "sm:col-span-2" : ""}>
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Password</Label>
            <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={update("password")} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12" />
          </div>
        </div>

        <Button data-testid="register-submit" disabled={loading} type="submit" className="w-full h-12 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white">{loading ? "Creating account…" : "Create account"}</Button>
        <p className="text-sm text-center text-[#5F6C60]">Already have an account? <Link to="/login" className="text-[#D97757] hover:underline">Sign in</Link></p>
      </form>
    </div>
  );
}
